import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useApp } from '../state/store';
import { navigate } from '../lib/router';
import { C, R, font } from '../theme/tokens';
import { myActivities, progress } from '../state/selectors';
import { ACTIVITY_TYPES, DEMO_NOTICE, HELP_ROLES } from '../data/seed';
import { userTimeZone, ymdIn } from '../lib/dates';
import { useNow } from '../lib/useNow';
import { isDemo } from '../lib/clock';
import type { Visibility } from '../domain/types';
import { MakeItHappen, type MakeItHappenAction } from './MakeItHappen';
import { BackPill, Btn, Chip, FieldError, FieldLabel, Mono, Segmented, Toggle, inputStyle, safeTop, textareaStyle } from '../components/ui';

type Mode = 'post' | 'propose' | 'request';
const MODES: Mode[] = ['post', 'propose', 'request'];

export function Create({ sub }: { sub?: string }) {
  const mode = MODES.includes(sub as Mode) ? (sub as Mode) : null;
  return mode ? <CreateForm key={mode} mode={mode} /> : <CreateMenu />;
}

function CreateMenu() {
  const { state: { data }, act } = useApp();
  const lead = myActivities(data)[0];
  const p = lead ? progress(data, lead.id) : null;
  // Each card opens the existing flow: the three Create forms, or the share kit
  // for the lead activity (demo falls back to the sample kit; api mode never invents one).
  const onAction = (a: MakeItHappenAction) => {
    if (a !== 'share') return navigate({ name: 'create', sub: a });
    const kit = lead?.shareKitId ?? (isDemo ? 'kit-london' : Object.keys(data.shareKits)[0]);
    return kit ? navigate({ name: 'share', id: kit }) : act.toast('No approved share kits yet. They arrive from Creative Studio (P2010).');
  };
  return (
    <div className="fade">
      <MakeItHappen onAction={onAction} assetBase={import.meta.env.BASE_URL}>
        {lead && p && (
          <div style={{ margin: '18px 12px 0', background: C.white, border: `1px solid ${C.subtle}`, borderRadius: R.card, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ font: font(600, 15, 1.25) }}>{`Update ${lead.name}`}</span>
              <span style={{ font: font(400, 12), color: C.grey }}>{`${p.done} / ${p.total} tasks · ${lead.health}`}</span>
            </span>
            <Btn variant="ink" onClick={() => act.openSheet({ kind: 'update', activityId: lead.id })}>Update progress</Btn>
          </div>
        )}
      </MakeItHappen>
    </div>
  );
}

/** Local-only media picker. Files never leave the device in demo mode. */
function MediaPicker({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    const u = files.filter((f) => f.type.startsWith('image/')).map((f) => URL.createObjectURL(f));
    setUrls(u);
    return () => u.forEach((x) => URL.revokeObjectURL(x));
  }, [files]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input ref={input} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => onChange(Array.from(e.target.files ?? []).slice(0, 6))} />
      <button type="button" onClick={() => (files.length ? onChange([]) : input.current?.click())}
        style={{ height: 50, borderRadius: R.input, border: `1px dashed ${files.length ? C.violet : C.border}`, background: files.length ? C.soft : C.white, color: files.length ? C.violet : C.grey, font: font(600, 13) }}>
        {files.length ? `${files.length} attached ✓ · tap to remove` : '+ Add photos or video'}
      </button>
      {urls.length > 0 && (
        <div style={{ display: 'flex', gap: 6 }}>{urls.map((u) => <img key={u} src={u} alt="Selected attachment" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10 }} />)}</div>
      )}
    </div>
  );
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FieldLabel>{label}</FieldLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
    </div>
  );
}

function CreateForm({ mode }: { mode: Mode }) {
  const { state: { data }, act } = useApp();
  const nowMs = useNow();
  const me = data.ambassadors[data.me];
  const mine = myActivities(data);
  const today = ymdIn(nowMs, me.timeZone);
  const [activityId, setActivityId] = useState(mine[0]?.id ?? '');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [visibility, setVisibility] = useState<'Network' | 'Team only'>('Network');
  const [name, setName] = useState('');
  const [type, setType] = useState<string>('Community meetup');
  const [unit, setUnit] = useState(me.units[0] ? `${me.units[0].name} · ${me.units[0].series}` : '');
  const [sending, setSending] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('18:00');
  const [place, setPlace] = useState('');
  const [needs, setNeeds] = useState<string[]>([]);
  const [announce, setAnnounce] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toggle = (xs: string[], x: string) => (xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x]);
  const short = (n: string) => n.replace('London Winter Community Campaign', 'Winter Campaign');

  const title = { post: 'Post update', propose: 'Propose activity', request: 'Request help' }[mode];
  const step = { post: 'CREATE · POST UPDATE', propose: 'CREATE · PROPOSE ACTIVITY', request: 'CREATE · REQUEST COLLABORATION' }[mode];

  function submit(e: FormEvent) {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (mode !== 'propose' && !activityId) err.activity = 'Pick an activity.';
    if (mode === 'post' && !text.trim()) err.text = 'Write a line about what happened.';
    if (mode === 'propose') {
      if (name.trim().length < 3) err.name = 'Give the activity a name of at least 3 characters.';
      if (!date) err.date = 'Pick a date.';
      else if (date < today) err.date = 'Pick today or a later date.';
    }
    if (mode === 'request' && !needs.length) err.needs = 'Pick at least one role.';
    setErrors(err);
    if (Object.keys(err).length) return;

    if (mode === 'post') {
      act.postUpdate({ activityId, text, mediaCount: files.length, visibility: (visibility === 'Network' ? 'network' : 'team') as Visibility });
      navigate({ name: 'home' });
    } else if (mode === 'propose') {
      const [unitName, unitSeries] = unit.split(' · ');
      // Affiliations carry the series label ("City UNITS") or id; resolve against the catalogue, never a hard-coded default.
      const series = data.series.find((s) => s.label === unitSeries || s.id === unitSeries)?.id ?? me.series;
      const tz = userTimeZone();
      const city = ['United Kingdom', me.name.split(' ')[0]].includes(unitName) ? me.city : unitName;
      const country = Object.values(data.activities).find((a) => a.city === city)?.country ?? 'United Kingdom';
      const local = new Date(`${date}T${time || '12:00'}:00`);
      setSending(true);
      void act.proposeActivity({
        name: name.trim(), type, series, city, country, timeZone: tz,
        startsAt: local.toISOString(), place: place.trim() || 'TBC', needs, brief: text.trim() || 'Proposed by you. Approval pending.',
      }, announce).then((id) => {
        setSending(false);
        if (id) navigate({ name: 'activity', id });
      });
    } else {
      act.requestHelp(activityId, needs, text);
      navigate({ name: 'collab', id: activityId });
    }
  }

  return (
    <form className="fade" onSubmit={submit} noValidate>
      <div style={{ padding: `${safeTop(12)} 16px 0`, display: 'flex', alignItems: 'center', gap: 10 }}>
        <BackPill label="‹ Create" onClick={() => navigate({ name: 'create' })} />
        <Mono size={10}>{step}</Mono>
      </div>
      <h1 style={{ margin: 0, padding: '16px 20px 0', font: font(800, 40, 0.92), letterSpacing: '-.05em' }}>{title}</h1>
      <div style={{ margin: '16px 12px 0', background: C.white, border: `1px solid ${C.subtle}`, borderRadius: R.card, padding: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {mode === 'propose' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <FieldLabel htmlFor="f-name">Activity name</FieldLabel>
              <input id="f-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="London Lantern Walk" aria-invalid={Boolean(errors.name)} style={inputStyle} />
              {errors.name && <FieldError>{errors.name}</FieldError>}
            </div>
            <Group label="Type">{ACTIVITY_TYPES.map((t) => <Chip key={t} label={t} selected={type === t} onClick={() => setType(t)} height={40} />)}</Group>
            <Group label="UNIT">{me.units.map((u) => { const v = `${u.name} · ${u.series}`; return <Chip key={v} label={v} selected={unit === v} onClick={() => setUnit(v)} height={40} />; })}</Group>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)', gap: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <FieldLabel htmlFor="f-date">Date</FieldLabel>
                <input id="f-date" type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} aria-invalid={Boolean(errors.date)} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <FieldLabel htmlFor="f-time">Time</FieldLabel>
                <input id="f-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} style={inputStyle} />
              </div>
            </div>
            {errors.date && <FieldError>{errors.date}</FieldError>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <FieldLabel htmlFor="f-place">Place (optional)</FieldLabel>
              <input id="f-place" value={place} onChange={(e) => setPlace(e.target.value)} maxLength={60} placeholder="South Bank" style={inputStyle} />
            </div>
            <Group label="Help needed">{HELP_ROLES.map((r) => <Chip key={r} label={r} selected={needs.includes(r)} onClick={() => setNeeds((n) => toggle(n, r))} height={40} />)}</Group>
          </>
        )}
        {mode !== 'propose' && (
          <Group label="Activity">{mine.map((a) => <Chip key={a.id} label={short(a.name)} selected={activityId === a.id} onClick={() => setActivityId(a.id)} height={40} />)}</Group>
        )}
        {errors.activity && <FieldError>{errors.activity}</FieldError>}
        {mode === 'request' && (
          <>
            <Group label="Who do you need?">{HELP_ROLES.map((r) => <Chip key={r} label={r} selected={needs.includes(r)} onClick={() => setNeeds((n) => toggle(n, r))} height={40} />)}</Group>
            {errors.needs && <FieldError>{errors.needs}</FieldError>}
          </>
        )}
        {mode === 'post' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <FieldLabel>Who can see this?</FieldLabel>
            <Segmented label="Visibility" options={['Network', 'Team only'] as const} value={visibility} onChange={setVisibility} />
            <span style={{ font: font(400, 12, 1.4), color: C.grey }}>{visibility === 'Network' ? 'All ambassadors on the UGAIN Pulse.' : 'Only the team of this activity.'}</span>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <FieldLabel htmlFor="f-text">{mode === 'post' ? 'What happened?' : mode === 'propose' ? 'Why it works (optional)' : 'Details (optional)'}</FieldLabel>
          <textarea id="f-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={400} aria-invalid={Boolean(errors.text)}
            placeholder={mode === 'post' ? 'Posters are up across Covent Garden.' : mode === 'propose' ? 'Lantern walk along the South Bank for new members.' : 'Two hours on Saturday afternoon.'} style={textareaStyle} />
          {errors.text && <FieldError>{errors.text}</FieldError>}
        </div>
        {mode === 'post' && <MediaPicker files={files} onChange={setFiles} />}
        {mode === 'propose' && <Toggle on={announce} onToggle={() => setAnnounce((x) => !x)} label="Announce on the UGAIN Pulse" sub="Ambassadors see the proposal and can offer help." />}
        <span style={{ font: font(400, 12, 1.45), color: C.grey }}>
          {mode === 'post' && 'Posts to the activity timeline and, if Network, the UGAIN Pulse.'}
          {mode === 'propose' && 'Goes to UNIT Marketing & Events for approval. You are named as the accountable organiser.'}
          {mode === 'request' && 'Posts to the UGAIN Pulse as Help wanted. You confirm offers in Collaboration.'}
        </span>
        <Btn type="submit" variant="primary" size="lg" full disabled={sending} aria-busy={sending}>
          {mode === 'post' ? (visibility === 'Network' ? 'Publish to Pulse' : 'Post to team') : mode === 'propose' ? 'Submit proposal' : needs.length ? `Post ${needs.length} request${needs.length > 1 ? 's' : ''}` : 'Post request'}
        </Btn>
        {isDemo && <span style={{ font: font(400, 11, 1.4), color: C.amber }}>{DEMO_NOTICE}</span>}
      </div>
    </form>
  );
}
